import React, { useState, useEffect, useMemo } from 'react';
import { Search, Plus, Edit2, Trash2, Save, X, Download, Upload } from 'lucide-react';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../ui/table";
import { confirmDialog } from "../ui/confirm-dialog";

const MachineInField = ({ axiosInstance, user }) => {
    // Filter States
    const [globalFilter, setGlobalFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [branchFilter, setBranchFilter] = useState('All');
    const [bdoFilter, setBdoFilter] = useState('All');
    const [tsoFilter, setTsoFilter] = useState('All');
    const [areaFilter, setAreaFilter] = useState('All'); // Area maps to City
    const [brandFilter, setBrandFilter] = useState('All');
    const [modelFilter, setModelFilter] = useState('All');
    const [warrantyFilter, setWarrantyFilter] = useState('All');

    // Placeholder Data
    const [accounts, setAccounts] = useState([]);

    // Editing State
    const [editingId, setEditingId] = useState(null);
    const [editFormData, setEditFormData] = useState({});

    // Fetch data on mount
    useEffect(() => {
        const fetchAccounts = async () => {
            if (!axiosInstance) return;
            try {
                const response = await axiosInstance.get('/mif/records');
                setAccounts(response.data);
            } catch (error) {
                console.error("Error fetching MIF records:", error);
            }
        };
        fetchAccounts();
    }, [axiosInstance]);

    // Extract unique values for dropdowns
    const uniqueValues = useMemo(() => {
        const getUnique = (key) => {
            const values = accounts.map(a => a[key]).filter(Boolean);
            return ['All', ...new Set(values)].sort();
        };

        return {
            status: getUnique('status'),
            branch: getUnique('branch'),
            bdo: getUnique('bdo'),
            tso: getUnique('tso'),
            area: getUnique('city'), // Area uses city field
            brand: getUnique('brand'),
            model: getUnique('model'),
            warranty: getUnique('warranty')
        };
    }, [accounts]);

    // Filter Logic
    const filteredAccounts = useMemo(() => {
        const filtered = accounts.filter(account => {
            // Global Search (all columns)
            const searchString = globalFilter.toLowerCase();
            const matchesGlobal = Object.values(account).some(val => 
                String(val).toLowerCase().includes(searchString)
            );

            // Date Filter
            const matchesDate = dateFilter ? (account.installationDate && account.installationDate.includes(dateFilter)) : true;

            // Dropdown Filters
            const matchesStatus = statusFilter === 'All' || account.status === statusFilter;
            const matchesBranch = branchFilter === 'All' || account.branch === branchFilter;
            const matchesBdo = bdoFilter === 'All' || account.bdo === bdoFilter;
            const matchesTso = tsoFilter === 'All' || account.tso === tsoFilter;
            const matchesArea = areaFilter === 'All' || account.city === areaFilter;
            const matchesBrand = brandFilter === 'All' || account.brand === brandFilter;
            const matchesModel = modelFilter === 'All' || account.model === modelFilter;
            const matchesWarranty = warrantyFilter === 'All' || account.warranty === warrantyFilter;

            return matchesGlobal && matchesDate && matchesStatus && matchesBranch && matchesBdo && matchesTso && matchesArea && matchesBrand && matchesModel && matchesWarranty;
        });

        // Sort by recent installationDate on top
        return filtered.sort((a, b) => {
            // Priority to new accounts (added via Add Account)
            if (a.isNew && !b.isNew) return -1;
            if (!a.isNew && b.isNew) return 1;
            
            const dateA = a.installationDate || '';
            const dateB = b.installationDate || '';
            if (dateA < dateB) return 1;
            if (dateA > dateB) return -1;
            return 0;
        });
    }, [accounts, globalFilter, dateFilter, statusFilter, branchFilter, bdoFilter, tsoFilter, areaFilter, brandFilter, modelFilter, warrantyFilter]);

    const handleExportCSV = () => {
        // Define CSV headers
        const headers = [
            "Status", "Account Name", "Installation Date", "Branch", "BDO", "TSO", 
            "Brand", "Model", "Serial #", "Warranty", "Address", "City", 
            "Contact Person", "Contact #", "Position", "Department"
        ];

        // Map data to CSV rows (using filteredAccounts or accounts? Usually export all, but user might want filtered. Let's stick to accounts for now to match original behavior, or switch to filtered if requested. Original used 'accounts'. Sticking to 'accounts' for full export unless specified otherwise.)
        const csvRows = [
            headers.join(','), // Header row
            ...accounts.map(account => {
                return [
                    `"${account.status || ''}"`,
                    `"${account.accountName || ''}"`,
                    `"${account.installationDate || ''}"`,
                    `"${account.branch || ''}"`,
                    `"${account.bdo || ''}"`,
                    `"${account.tso || ''}"`,
                    `"${account.brand || ''}"`,
                    `"${account.model || ''}"`,
                    `"${account.serialNumber || ''}"`,
                    `"${account.warranty || ''}"`,
                    `"${account.address || ''}"`,
                    `"${account.city || ''}"`,
                    `"${account.contactPerson || ''}"`,
                    `"${account.contactNumber || ''}"`,
                    `"${account.position || ''}"`,
                    `"${account.department || ''}"`
                ].join(',');
            })
        ];

        // Create CSV file and trigger download
        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `mif_accounts_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const handleImportCSV = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (e) => {
            const text = e.target.result;
            // Handle different line endings
            const rows = text.split(/\r?\n/);
            
            // Assuming first row is header, skip it
            const dataRows = rows.slice(1);
            const parsedRecords = [];

            // Robust CSV Line Parser
            const parseCSVLine = (line) => {
                const result = [];
                let start = 0;
                let inQuotes = false;
                for (let i = 0; i < line.length; i++) {
                    if (line[i] === '"') {
                        inQuotes = !inQuotes;
                    } else if (line[i] === ',' && !inQuotes) {
                        let field = line.substring(start, i);
                        // Remove surrounding quotes and handle escaped quotes
                        if (field.startsWith('"') && field.endsWith('"')) {
                            field = field.slice(1, -1).replace(/""/g, '"');
                        }
                        result.push(field);
                        start = i + 1;
                    }
                }
                // Push the last field
                let lastField = line.substring(start);
                if (lastField.startsWith('"') && lastField.endsWith('"')) {
                    lastField = lastField.slice(1, -1).replace(/""/g, '"');
                }
                result.push(lastField);
                return result;
            };

            for (const row of dataRows) {
                if (!row.trim()) continue; // Skip empty rows
                
                const cleanCols = parseCSVLine(row);

                // Map columns to object keys (order must match export)
                // We check for >= 16 columns. Note: parseCSVLine will preserve empty columns.
                if (cleanCols.length >= 16) {
                     parsedRecords.push({
                        status: cleanCols[0],
                        accountName: cleanCols[1],
                        installationDate: cleanCols[2],
                        branch: cleanCols[3],
                        bdo: cleanCols[4],
                        tso: cleanCols[5],
                        brand: cleanCols[6],
                        model: cleanCols[7],
                        serialNumber: cleanCols[8],
                        warranty: cleanCols[9],
                        address: cleanCols[10],
                        city: cleanCols[11],
                        contactPerson: cleanCols[12],
                        contactNumber: cleanCols[13],
                        position: cleanCols[14],
                        department: cleanCols[15]
                    });
                }
            }

            if (parsedRecords.length > 0 && axiosInstance) {
                try {
                    await axiosInstance.post('/mif/import', parsedRecords);
                    // Refresh data
                    const response = await axiosInstance.get('/mif/records');
                    setAccounts(response.data);
                    alert(`Successfully imported ${parsedRecords.length} records.`);
                } catch (error) {
                    console.error("Error importing CSV:", error);
                    alert("Failed to import CSV. Please check the file format.");
                }
            }
        };
        reader.readAsText(file);
        // Reset input
        event.target.value = '';
    };

    const handleClearFilters = () => {
        setGlobalFilter('');
        setDateFilter('');
        setStatusFilter('All');
        setBranchFilter('All');
        setBdoFilter('All');
        setTsoFilter('All');
        setAreaFilter('All');
        setBrandFilter('All');
        setModelFilter('All');
        setWarrantyFilter('All');
    };

    const handleAddAccount = () => {
        const newId = `temp-${Date.now()}`;
        const newAccount = {
            id: newId,
            status: '',
            accountName: '',
            installationDate: '',
            branch: '',
            bdo: '',
            tso: '',
            brand: '',
            model: '',
            serialNumber: '',
            warranty: '',
            address: '',
            city: '',
            contactPerson: '',
            contactNumber: '',
            position: '',
            department: '',
            isNew: true // Flag to identify new row
        };
        setAccounts([newAccount, ...accounts]);
        setEditingId(newId);
        setEditFormData(newAccount);
    };

    const handleEditClick = (account) => {
        setEditingId(account.id);
        setEditFormData(account);
    };

    const handleCancelClick = () => {
        if (editingId) {
            const editingAccount = accounts.find(a => a.id === editingId);
            if (editingAccount && editingAccount.isNew) {
                // If cancelling a new row, remove it
                setAccounts(accounts.filter(a => a.id !== editingId));
            }
        }
        setEditingId(null);
        setEditFormData({});
    };

    const handleSaveClick = async () => {
        if (!axiosInstance) return;
        try {
            if (editFormData.isNew) {
                // Create
                const { id, isNew, createdAt, updatedAt, ...dataToSave } = editFormData;
                const response = await axiosInstance.post('/mif/records', dataToSave);
                const savedAccount = response.data;
                
                setAccounts(accounts.map(account => 
                    account.id === editingId ? savedAccount : account
                ));
            } else {
                // Update
                const { id, isNew, createdAt, updatedAt, ...dataToSave } = editFormData;
                const response = await axiosInstance.put(`/mif/records/${id}`, dataToSave);
                const savedAccount = response.data;
                
                setAccounts(accounts.map(account => 
                    account.id === editingId ? savedAccount : account
                ));
            }
            setEditingId(null);
            setEditFormData({});
        } catch (error) {
            console.error("Error saving account:", error);
        }
    };

    const handleDeleteClick = async (id) => {
        if (!axiosInstance) return;
        try {
            if (String(id).startsWith('temp-')) {
                 setAccounts(accounts.filter(account => account.id !== id));
                 return;
            }
            
            await axiosInstance.delete(`/mif/records/${id}`);
            setAccounts(accounts.filter(account => account.id !== id));
        } catch (error) {
             console.error("Error deleting account:", error);
        }
    };

    const handleWipeDatabase = async () => {
        const confirm1 = await confirmDialog({
            title: "Wipe Database",
            description: "WARNING: This will delete ALL data in the Machine in Field database. This action cannot be undone. Are you sure?",
            variant: "destructive",
            confirmText: "Yes, wipe data"
        });
        if (confirm1) {
            const confirm2 = await confirmDialog({
                title: "Final Warning",
                description: "Are you REALLY sure? This will permanently delete all records.",
                variant: "destructive",
                confirmText: "Permanently Delete"
            });
            if (confirm2) {
                try {
                    await axiosInstance.delete('/mif/records/wipe');
                    setAccounts([]);
                    alert("Database wiped successfully.");
                } catch (error) {
                    console.error("Error wiping database:", error);
                    alert("Failed to wipe database: " + (error.response?.data?.detail || error.message));
                }
            }
        }
    };

    const handleInputChange = (e, field) => {
        setEditFormData({ ...editFormData, [field]: e.target.value });
    };

    return (
        <div className="h-full flex flex-col space-y-4">
            {/* Top Controls Bar */}
            <div
                className="p-4 rounded-2xl space-y-4"
                style={{
                    background: 'var(--nad-s1)',
                    border: '1px solid var(--nad-b1)',
                }}
            >
                {/* Row 1: Search Bars & Add Button */}
                <div className="flex flex-wrap gap-4 items-center justify-between">
                    <div className="flex gap-4 flex-1">
                        <div className="relative flex-1 max-w-sm">
                            <Search className="absolute left-2 top-2.5 h-4 w-4" style={{ color: 'var(--nad-t4)' }} />
                            <Input
                                placeholder="Search all columns..."
                                value={globalFilter}
                                onChange={(e) => setGlobalFilter(e.target.value)}
                                className="pl-8"
                                style={{
                                    background: 'var(--nad-s2)',
                                    borderColor: 'var(--nad-b1)',
                                    color: 'var(--nad-t1)',
                                }}
                            />
                        </div>
                        <div className="relative flex-1 max-w-sm">
                            <Input
                                type="date"
                                placeholder="Filter by Date..."
                                value={dateFilter}
                                onChange={(e) => setDateFilter(e.target.value)}
                                style={{
                                    background: 'var(--nad-s2)',
                                    borderColor: 'var(--nad-b1)',
                                    color: 'var(--nad-t1)',
                                }}
                            />
                        </div>
                    </div>
                    <div className="flex gap-2">
                         <input 
                            type="file" 
                            accept=".csv" 
                            onChange={handleImportCSV} 
                            style={{ display: 'none' }} 
                            id="csv-upload" 
                        />
                        <Button 
                            variant="outline"
                            onClick={() => document.getElementById('csv-upload').click()}
                            className="gap-2"
                            style={{
                                background: 'var(--nad-s2)',
                                borderColor: 'var(--nad-b1)',
                                color: 'var(--nad-t2)',
                            }}
                        >
                            <Upload size={16} />
                            Import CSV
                        </Button>
                        <Button 
                            variant="outline"
                            onClick={handleExportCSV}
                            className="gap-2"
                            style={{
                                background: 'var(--nad-s2)',
                                borderColor: 'var(--nad-b1)',
                                color: 'var(--nad-t2)',
                            }}
                        >
                            <Download size={16} />
                            Export CSV
                        </Button>
                        {user?.email === "vinsimon23@gmail.com" && (
                            <Button 
                                onClick={handleWipeDatabase}
                                className="gap-2"
                                style={{
                                    background: 'linear-gradient(to bottom right, rgba(248,113,113,0.28), rgba(248,113,113,0.18))',
                                    border: '1px solid rgba(248,113,113,0.35)',
                                    color: 'var(--nad-neg)',
                                }}
                            >
                                <Trash2 size={16} />
                                Wipe Database
                            </Button>
                        )}
                        <Button 
                            onClick={handleAddAccount}
                            className="gap-2"
                            style={{
                                background: 'linear-gradient(to bottom right, rgba(59,130,246,0.28), rgba(250,204,21,0.20))',
                                border: '1px solid rgba(59,130,246,0.35)',
                                color: 'var(--nad-accent-txt)',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(to bottom right, rgba(59,130,246,0.40), rgba(250,204,21,0.32))';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(to bottom right, rgba(59,130,246,0.28), rgba(250,204,21,0.20))';
                            }}
                        >
                            <Plus size={16} />
                            Add Account
                        </Button>
                    </div>
                </div>

                {/* Row 2: Dropdown Filters */}
                <div className="flex flex-wrap gap-4 items-center">
                    <div className="w-[180px]">
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger style={{ background: 'var(--nad-s2)', borderColor: 'var(--nad-b1)', color: 'var(--nad-t1)' }}>
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent style={{ background: 'var(--nad-panel-bg)', borderColor: 'var(--nad-b1)', color: 'var(--nad-t1)' }}>
                                {uniqueValues.status.map(val => (
                                    <SelectItem key={val} value={val}>{val === 'All' ? 'All Status' : val}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="w-[180px]">
                        <Select value={branchFilter} onValueChange={setBranchFilter}>
                            <SelectTrigger style={{ background: 'var(--nad-s2)', borderColor: 'var(--nad-b1)', color: 'var(--nad-t1)' }}>
                                <SelectValue placeholder="Branch" />
                            </SelectTrigger>
                            <SelectContent style={{ background: 'var(--nad-panel-bg)', borderColor: 'var(--nad-b1)', color: 'var(--nad-t1)' }}>
                                {uniqueValues.branch.map(val => (
                                    <SelectItem key={val} value={val}>{val === 'All' ? 'All Branches' : val}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="w-[180px]">
                        <Select value={bdoFilter} onValueChange={setBdoFilter}>
                            <SelectTrigger style={{ background: 'var(--nad-s2)', borderColor: 'var(--nad-b1)', color: 'var(--nad-t1)' }}>
                                <SelectValue placeholder="BDO" />
                            </SelectTrigger>
                            <SelectContent style={{ background: 'var(--nad-panel-bg)', borderColor: 'var(--nad-b1)', color: 'var(--nad-t1)' }}>
                                {uniqueValues.bdo.map(val => (
                                    <SelectItem key={val} value={val}>{val === 'All' ? 'All BDOs' : val}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="w-[180px]">
                        <Select value={tsoFilter} onValueChange={setTsoFilter}>
                            <SelectTrigger style={{ background: 'var(--nad-s2)', borderColor: 'var(--nad-b1)', color: 'var(--nad-t1)' }}>
                                <SelectValue placeholder="TSO" />
                            </SelectTrigger>
                            <SelectContent style={{ background: 'var(--nad-panel-bg)', borderColor: 'var(--nad-b1)', color: 'var(--nad-t1)' }}>
                                {uniqueValues.tso.map(val => (
                                    <SelectItem key={val} value={val}>{val === 'All' ? 'All TSOs' : val}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="w-[180px]">
                        <Select value={areaFilter} onValueChange={setAreaFilter}>
                            <SelectTrigger style={{ background: 'var(--nad-s2)', borderColor: 'var(--nad-b1)', color: 'var(--nad-t1)' }}>
                                <SelectValue placeholder="Area" />
                            </SelectTrigger>
                            <SelectContent style={{ background: 'var(--nad-panel-bg)', borderColor: 'var(--nad-b1)', color: 'var(--nad-t1)' }}>
                                {uniqueValues.area.map(val => (
                                    <SelectItem key={val} value={val}>{val === 'All' ? 'All Areas' : val}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="w-[180px]">
                        <Select value={brandFilter} onValueChange={setBrandFilter}>
                            <SelectTrigger style={{ background: 'var(--nad-s2)', borderColor: 'var(--nad-b1)', color: 'var(--nad-t1)' }}>
                                <SelectValue placeholder="Brand" />
                            </SelectTrigger>
                            <SelectContent style={{ background: 'var(--nad-panel-bg)', borderColor: 'var(--nad-b1)', color: 'var(--nad-t1)' }}>
                                {uniqueValues.brand.map(val => (
                                    <SelectItem key={val} value={val}>{val === 'All' ? 'All Brands' : val}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="w-[180px]">
                        <Select value={modelFilter} onValueChange={setModelFilter}>
                            <SelectTrigger style={{ background: 'var(--nad-s2)', borderColor: 'var(--nad-b1)', color: 'var(--nad-t1)' }}>
                                <SelectValue placeholder="Model" />
                            </SelectTrigger>
                            <SelectContent style={{ background: 'var(--nad-panel-bg)', borderColor: 'var(--nad-b1)', color: 'var(--nad-t1)' }}>
                                {uniqueValues.model.map(val => (
                                    <SelectItem key={val} value={val}>{val === 'All' ? 'All Models' : val}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="w-[180px]">
                        <Select value={warrantyFilter} onValueChange={setWarrantyFilter}>
                            <SelectTrigger style={{ background: 'var(--nad-s2)', borderColor: 'var(--nad-b1)', color: 'var(--nad-t1)' }}>
                                <SelectValue placeholder="Warranty" />
                            </SelectTrigger>
                            <SelectContent style={{ background: 'var(--nad-panel-bg)', borderColor: 'var(--nad-b1)', color: 'var(--nad-t1)' }}>
                                {uniqueValues.warranty.map(val => (
                                    <SelectItem key={val} value={val}>{val === 'All' ? 'All Warranty' : val}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <Button 
                        variant="ghost" 
                        onClick={handleClearFilters}
                        style={{ color: 'var(--nad-neg)' }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(248,113,113,0.12)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                        }}
                    >
                        Clear Filters
                    </Button>
                </div>
            </div>

            {/* Main Content / Table Area */}
            <div
                className="rounded-2xl flex-1 overflow-hidden flex flex-col"
                style={{
                    background: 'var(--nad-s1)',
                    border: '1px solid var(--nad-b1)',
                }}
            >
                 <div
                    className="p-4 border-b"
                    style={{
                        borderColor: 'var(--nad-b1)',
                        background: 'linear-gradient(to right, rgba(59,130,246,0.08), rgba(250,204,21,0.04))',
                    }}
                >
                    <h3 className="font-semibold" style={{ color: 'var(--nad-t1)' }}>
                        MIF Accounts List ({filteredAccounts.length})
                    </h3>
                </div>
                <div className="overflow-auto flex-1 p-0" style={{ background: 'var(--nad-s2)' }}>
                    <Table>
                        <TableHeader>
                            <TableRow style={{ background: 'var(--nad-s1)' }}>
                                {['Status','Account Name','Installation Date','Branch','BDO','TSO','Brand','Model','Serial #','Warranty','Address','City','Contact Person','Contact #','Position','Department'].map((h) => (
                                    <TableHead key={h} style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>{h}</TableHead>
                                ))}
                                <TableHead className="w-[100px] text-right" style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredAccounts.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={17} className="text-center py-8" style={{ color: 'var(--nad-t3)', borderColor: 'var(--nad-b1)' }}>
                                        {accounts.length === 0 
                                            ? "No accounts found. Click \"Add Account\" to create one." 
                                            : "No accounts found matching your filters."}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredAccounts.map((account) => (
                                    <TableRow
                                        key={account.id}
                                        onMouseEnter={(e) => {
                                            if (editingId !== account.id) {
                                                e.currentTarget.style.background = 'var(--nad-s1)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (editingId !== account.id) {
                                                e.currentTarget.style.background = 'transparent';
                                            }
                                        }}
                                        style={{ borderColor: 'var(--nad-b1)' }}
                                    >
                                        {editingId === account.id ? (
                                            <>
                                                {[
                                                    ['status', 'text'],
                                                    ['accountName', 'text'],
                                                    ['installationDate', 'date'],
                                                    ['branch', 'text'],
                                                    ['bdo', 'text'],
                                                    ['tso', 'text'],
                                                    ['brand', 'text'],
                                                    ['model', 'text'],
                                                    ['serialNumber', 'text'],
                                                    ['warranty', 'text'],
                                                    ['address', 'text'],
                                                    ['city', 'text'],
                                                    ['contactPerson', 'text'],
                                                    ['contactNumber', 'text'],
                                                    ['position', 'text'],
                                                    ['department', 'text'],
                                                ].map(([field, type]) => (
                                                    <TableCell key={field} style={{ borderColor: 'var(--nad-b1)' }}>
                                                        <Input
                                                            type={type}
                                                            value={editFormData[field] || ''}
                                                            onChange={(e) => handleInputChange(e, field)}
                                                            className="h-8 w-full"
                                                            style={{
                                                                background: 'var(--nad-s2)',
                                                                borderColor: 'var(--nad-b1)',
                                                                color: 'var(--nad-t1)',
                                                            }}
                                                        />
                                                    </TableCell>
                                                ))}
                                                <TableCell className="text-right" style={{ borderColor: 'var(--nad-b1)' }}>
                                                    <div className="flex justify-end gap-2">
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={handleSaveClick}
                                                            className="h-8 w-8"
                                                            style={{ color: 'rgb(74,222,128)' }}
                                                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(34,197,94,0.12)'; }}
                                                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                                        >
                                                            <Save size={16} />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={handleCancelClick}
                                                            className="h-8 w-8"
                                                            style={{ color: 'var(--nad-neg)' }}
                                                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(248,113,113,0.12)'; }}
                                                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                                        >
                                                            <X size={16} />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </>
                                        ) : (
                                            <>
                                                <TableCell style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>{account.status}</TableCell>
                                                <TableCell className="font-medium" style={{ color: 'var(--nad-t1)', borderColor: 'var(--nad-b1)' }}>{account.accountName}</TableCell>
                                                <TableCell style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>{account.installationDate}</TableCell>
                                                <TableCell style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>{account.branch}</TableCell>
                                                <TableCell style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>{account.bdo}</TableCell>
                                                <TableCell style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>{account.tso}</TableCell>
                                                <TableCell style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>{account.brand}</TableCell>
                                                <TableCell style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>{account.model}</TableCell>
                                                <TableCell style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>{account.serialNumber}</TableCell>
                                                <TableCell style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>{account.warranty}</TableCell>
                                                <TableCell style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>{account.address}</TableCell>
                                                <TableCell style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>{account.city}</TableCell>
                                                <TableCell style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>{account.contactPerson}</TableCell>
                                                <TableCell style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>{account.contactNumber}</TableCell>
                                                <TableCell style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>{account.position}</TableCell>
                                                <TableCell style={{ color: 'var(--nad-t2)', borderColor: 'var(--nad-b1)' }}>{account.department}</TableCell>
                                                <TableCell className="text-right" style={{ borderColor: 'var(--nad-b1)' }}>
                                                    <div className="flex justify-end gap-2">
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleEditClick(account)}
                                                            className="h-8 w-8"
                                                            style={{ color: 'rgb(96,165,250)' }}
                                                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(59,130,246,0.12)'; }}
                                                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                                        >
                                                            <Edit2 size={16} />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleDeleteClick(account.id)}
                                                            className="h-8 w-8"
                                                            style={{ color: 'var(--nad-neg)' }}
                                                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(248,113,113,0.12)'; }}
                                                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                                        >
                                                            <Trash2 size={16} />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </>
                                        )}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </div>
    );
};

export default MachineInField;